import React, { useEffect } from 'react'
import cx from 'classnames'
import { Marker, Polygon, Polyline } from 'react-leaflet'

import { Button } from 'components/inputs'
import Popup from 'compositions/map/MapContent/components/Popup/Popup'
import { getMarkerIcon } from 'compositions/map/MapContent/MapContent'

import s from './EditableMapContent.scss'

import { transformLatLngsToCoords, useEditableMapContent } from './util'


const EditableMapContent: React.FC<EditableMap.MapContentProps> = (props) => {
  const {
    placemarks,
    fixedPlacemarks,
    polylines,
    multiPolygons,
    placemarksField,
    polylinesField,
    multiPolygonsField,
  } = props

  const {
    map,
    selectedElement,
    selectedElementState,
    selectedElementId,
    markerIcon,
    handleDelete,
    handleGeometryClick,
    handleGeometryUpdate,
    handleToggleEdit,
    handleMapClick,
  } = useEditableMapContent(placemarks)

  const placemarkRef = React.useRef(null)

  useEffect(() => {
    if (placemarks?.length === 1) {
      placemarkRef.current?._icon?.click()
    }
  }, [])

  return (
    <>
      {
        selectedElement.current && selectedElementState?.[selectedElementId.current]?.deletable && (
          <Button
            className={cx(s.shadow, 'ml-24px mt-16px z-800')}
            style="secondary"
            title="Удалить"
            icon="main/delete_16"
            size={40}
            onClick={(event) => {
              event.stopPropagation()
              handleDelete()
            }}
          />
        )
      }
      {
        placemarks?.map(({ id, geometry, title, text, editOn, centerOnUpdate }, index) => (
          <Marker
            ref={index === 0 ? placemarkRef : null}
            key={id}
            position={geometry}
            icon={markerIcon}
            zIndexOffset={fixedPlacemarks?.length ? fixedPlacemarks.length + 1000 : undefined}
            bubblingMouseEvents={false}
            eventHandlers={{
              'click': (event) => handleGeometryClick(event, placemarksField, placemarks, id),
              'editable:dragend': (e) => {
                const newGeometry = transformLatLngsToCoords(e.target.getLatLng())
                handleGeometryUpdate(placemarksField, placemarks, id, newGeometry)
              },
              'add': (e) => {
                if (editOn) {
                  handleToggleEdit(e)
                }
              },
            }}
          />
        ))
      }
      {
        fixedPlacemarks?.map(({ id, icon, geometry, color, popupData, visible = true }, index) => {
          const markerIcon = getMarkerIcon(icon, color)
          return (
            visible && (
              <Marker
                key={id}
                position={geometry}
                icon={markerIcon}
                bubblingMouseEvents={false}
              >
                {
                  Boolean(popupData) && (
                    <Popup
                      data={popupData}
                    />
                  )
                }
              </Marker>
            )
          )
        })
      }
      {
        polylines?.map(({ id, geometry, color = '#3d75e4', editOn }) => (
          <Polyline
            key={id}
            positions={geometry}
            bubblingMouseEvents={false}
            pathOptions={{
              color: color,
              weight: 1,
            }}
            eventHandlers={{
              'click': (event) => handleGeometryClick.call(this, event, polylinesField, polylines, id),
              'editable:dragend': (e) => {
                const newGeometry = transformLatLngsToCoords(e.target.getLatLngs())
                handleGeometryUpdate(polylinesField, polylines, id, newGeometry)
              },
              'editable:vertex:dragstart': (e) => {
                // workaround of this bug https://github.com/Leaflet/Leaflet/issues/4457
                map.off('click', handleMapClick)
              },
              'editable:vertex:dragend': (e) => {
                setTimeout(() => map.on('click', handleMapClick), 100) // off bug workaround
                const newGeometry = transformLatLngsToCoords(e.target.getLatLngs())
                handleGeometryUpdate(polylinesField, polylines, id, newGeometry)
              },
              'editable:drawing:commit': (e) => {
                const newGeometry = transformLatLngsToCoords(e.target.getLatLngs())
                handleGeometryUpdate(polylinesField, polylines, id, newGeometry)
              },
              'add': (e) => {
                if (editOn) {
                  handleToggleEdit(e)
                }
              },
            }}
          />
        ))
      }
      {
        multiPolygons?.map(({ id, geometry, color = '#3d75e4', editOn }) => (
          <Polygon
            key={id}
            positions={geometry}
            bubblingMouseEvents={false}
            pathOptions={{
              color: color,
              fillOpacity: 0.16,
              weight: 1,
            }}
            eventHandlers={{
              'click': (event) => handleGeometryClick.call(this, event, multiPolygonsField, multiPolygons, id),
              'editable:dragend': (e) => {
                const newGeometry = transformLatLngsToCoords(e.target.getLatLngs())
                handleGeometryUpdate(multiPolygonsField, multiPolygons, id, newGeometry)
              },
              'editable:vertex:dragstart': (e) => {
                // workaround of this bug https://github.com/Leaflet/Leaflet/issues/4457
                map.off('click', handleMapClick)
              },
              'editable:vertex:dragend': (e) => {
                setTimeout(() => map.on('click', handleMapClick), 100) // off bug workaround
                const newGeometry = transformLatLngsToCoords(e.target.getLatLngs())
                handleGeometryUpdate(multiPolygonsField, multiPolygons, id, newGeometry)
              },
              'editable:drawing:commit': (e) => {
                const newGeometry = transformLatLngsToCoords(e.target.getLatLngs())
                handleGeometryUpdate(multiPolygonsField, multiPolygons, id, newGeometry)
              },
              'add': (e) => {
                if (editOn) {
                  handleToggleEdit(e)
                }
              },
            }}
          />
        ))
      }
    </>
  )
}

export default EditableMapContent